import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  LayoutDashboard,
  Users,
  Calendar,
  LogOut,
  DollarSign,
  BookOpen,
  Building2,
  Globe,
  Sparkles,
  Loader2,
  UserCheck,
  Briefcase,
  ListChecks,
  FileSignature,
  Menu,
  X,
  Cloud,
  RefreshCw,
  Layers,
  AlertCircle,
} from 'lucide-react';

import {
  Student,
  UserRole,
  Branch,
  Employee,
  Tentor,
  Schedule,
  BranchIncomeRecord,
  ExpenseRecord,
  PendingPayment,
  OnlineRegistration,
} from './types/db';

import { Dashboard } from './components/Dashboard';
import { StudentList } from './components/StudentList';
import {
  ScheduleManager,
  ExamManager,
  AttendanceManager,
  ReportManager,
  ModuleManager,
  TeacherManager,
} from './components/academic';
import { ClassManager } from './components/academic/ClassManager';
import { FinanceManager } from './components/finance/FinanceManager';
import { StudentPortal } from './components/StudentPortal';
import { BranchManager } from './components/branch/BranchManager';
import { UserManager } from './components/user/UserManager';
import { WebsiteBuilder } from './components/website/WebsiteBuilder';
import { PublicLayout } from './components/PublicLayout';
import { supabase } from './lib/supabaseClient';
import { useAuth } from './context/AuthContext';

const App = () => {
  const { profile, loading, logout } = useAuth();

  const [currentView, setCurrentView] = useState<string>('DASHBOARD');
  const [selectedBranchId, setSelectedBranchId] = useState('ALL');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const hasInitializedFilter = useRef(false);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [tentors, setTentors] = useState<Tentor[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [onlineRegistrations, setOnlineRegistrations] = useState<OnlineRegistration[]>([]);
  const [incomes, setIncomes] = useState<BranchIncomeRecord[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  useEffect(() => {
    const {
      data: { subscription },
    } = (supabase.auth as any).onAuthStateChange((event: string) => {
      if (event === 'SIGNED_OUT') {
        window.location.reload();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const studentData = useMemo(() => {
    if (!profile || profile.role !== UserRole.STUDENT) return null;
    return (
      students.find(
        (s) => s.user_id === profile.id || (profile.email && s.email === profile.email)
      ) || null
    );
  }, [students, profile]);

  const isHQRole = useMemo(
    () =>
      profile &&
      [UserRole.MASTER_ADMIN, UserRole.SUPER_ADMIN, UserRole.CENTRAL_ADMIN].includes(profile.role),
    [profile]
  );

  const isAdminManager = useMemo(
    () => profile && [UserRole.MASTER_ADMIN, UserRole.SUPER_ADMIN].includes(profile.role),
    [profile]
  );

  const fetchData = useCallback(async () => {
    if (!profile) return;
    setIsSyncing(true);

    const {
      data: { session },
    } = await (supabase.auth as any).getSession();
    if (!session) {
      setIsSyncing(false);
      return;
    }

    try {
      const { data: bData } = await supabase.from('branches').select('*');
      if (bData) setBranches(bData.filter((b: Branch) => b.id !== 'HEADQUARTERS' && b.id !== 'HQ'));

      const branchFilter = isHQRole
        ? selectedBranchId === 'ALL'
          ? null
          : selectedBranchId
        : profile.branch_id;

      const results = await Promise.all([
        supabase.from('students').select('*, class_room:classes(*)').order('name'),
        supabase.from('branch_incomes').select('*').order('date', { ascending: false }),
        supabase.from('expenses').select('*').order('date', { ascending: false }),
        supabase.from('pending_payments').select('*').eq('status', 'PENDING'),
        supabase.from('online_registrations').select('*').eq('status', 'PENDING'),
        supabase.from('tentors').select('*'),
        supabase.from('employees').select('*'),
        supabase.from('schedules').select('*, class_room:classes(*)'),
        supabase.from('exams').select('*'),
        supabase.from('learning_modules').select('*'),
      ]);

      const applyFilter = (data: any[]) =>
        branchFilter ? data.filter((i) => i.branch_id === branchFilter) : data;

      setStudents(applyFilter(results[0].data || []));
      setIncomes(applyFilter(results[1].data || []));
      setExpenses(applyFilter(results[2].data || []));
      setPendingPayments(applyFilter(results[3].data || []));
      setOnlineRegistrations(applyFilter(results[4].data || []));
      setTentors(applyFilter(results[5].data || []));
      setEmployees(applyFilter(results[6].data || []));
      setSchedules(applyFilter(results[7].data || []));
    } catch (err: any) {
      /* console removed */
    } finally {
      setIsSyncing(false);
    }
  }, [profile, selectedBranchId, isHQRole]);

  useEffect(() => {
    if (profile && !hasInitializedFilter.current) {
      setSelectedBranchId(isHQRole ? 'ALL' : profile.branch_id || 'HEADQUARTERS');
      hasInitializedFilter.current = true;
    }
  }, [profile, isHQRole]);

  useEffect(() => {
    if (profile && hasInitializedFilter.current) fetchData();
  }, [profile, selectedBranchId, fetchData]);

  if (loading)
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white gap-4">
        <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">
          Delta Cloud Syncing...
        </p>
      </div>
    );

  if (!profile) return <PublicLayout />;

  const activeBranchName =
    selectedBranchId === 'ALL'
      ? 'KONSOLIDASI JARINGAN'
      : selectedBranchId === 'HEADQUARTERS'
        ? 'KANTOR PUSAT (HQ)'
        : branches.find((b) => b.id === selectedBranchId)?.name || 'Unit Cabang';

  const navigateTo = (view: string) => {
    setCurrentView(view);
    setIsSidebarOpen(false);
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC] font-sans overflow-hidden relative">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-[90] md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`fixed inset-y-0 left-0 z-[100] w-64 bg-white border-r border-slate-100 flex flex-col shadow-xl md:shadow-sm transition-transform duration-300 transform md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-6 border-b border-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-lg shadow-indigo-100">
              Δ
            </div>
            <div>
              <div className="font-black text-base text-slate-800 leading-none uppercase">
                Delta
              </div>
              <div className="text-[7px] font-black text-slate-400 uppercase tracking-widest mt-1">
                Cloud Secure
              </div>
            </div>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-slate-400 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto no-scrollbar">
          <SidebarItem
            icon={<LayoutDashboard className="w-4 h-4" />}
            label="Dashboard"
            active={currentView === 'DASHBOARD'}
            onClick={() => navigateTo('DASHBOARD')}
          />
          {profile.role !== UserRole.STUDENT && (
            <>
              {profile.role !== UserRole.TENTOR &&
                (isHQRole || [UserRole.FINANCE, UserRole.ACADEMIC].includes(profile.role)) && (
                  <SidebarItem
                    icon={<Users className="w-4 h-4" />}
                    label="Data Siswa"
                    active={currentView === 'STUDENTS'}
                    onClick={() => navigateTo('STUDENTS')}
                  />
                )}

              {(isHQRole || [UserRole.ACADEMIC, UserRole.TENTOR].includes(profile.role)) && (
                <>
                  <SidebarItem
                    icon={<Calendar className="w-4 h-4" />}
                    label="Jadwal Kelas"
                    active={currentView === 'SCHEDULE'}
                    onClick={() => navigateTo('SCHEDULE')}
                  />

                  {profile.role !== UserRole.TENTOR && (
                    <>
                      <SidebarItem
                        icon={<Layers className="w-4 h-4" />}
                        label="Manajemen Kelas"
                        active={currentView === 'CLASS_ROOMS'}
                        onClick={() => navigateTo('CLASS_ROOMS')}
                      />
                      <SidebarItem
                        icon={<ListChecks className="w-4 h-4" />}
                        label="Input Absensi"
                        active={currentView === 'ATTENDANCE'}
                        onClick={() => navigateTo('ATTENDANCE')}
                      />
                      <SidebarItem
                        icon={<Sparkles className="w-4 h-4" />}
                        label="Ujian AI"
                        active={currentView === 'EXAMS'}
                        onClick={() => navigateTo('EXAMS')}
                      />
                      <SidebarItem
                        icon={<BookOpen className="w-4 h-4" />}
                        label="E-Modules"
                        active={currentView === 'MODULES'}
                        onClick={() => navigateTo('MODULES')}
                      />
                    </>
                  )}

                  <SidebarItem
                    icon={<FileSignature className="w-4 h-4" />}
                    label="Rapor Digital AI"
                    active={currentView === 'REPORTS'}
                    onClick={() => navigateTo('REPORTS')}
                  />
                </>
              )}

              {profile.role !== UserRole.TENTOR && (
                <>
                  {(isHQRole || profile.role === UserRole.ACADEMIC) && (
                    <SidebarItem
                      icon={<Briefcase className="w-4 h-4" />}
                      label="Database Tentor"
                      active={currentView === 'TEACHERS'}
                      onClick={() => navigateTo('TEACHERS')}
                    />
                  )}
                  {(isHQRole || profile.role === UserRole.FINANCE) && (
                    <SidebarItem
                      icon={<DollarSign className="w-4 h-4" />}
                      label="Keuangan"
                      active={currentView === 'FINANCE'}
                      onClick={() => navigateTo('FINANCE')}
                    />
                  )}
                </>
              )}

              {isAdminManager && (
                <>
                  <div className="pt-4 pb-2 px-4 text-[8px] font-black text-slate-300 uppercase tracking-widest">
                    Management
                  </div>
                  <SidebarItem
                    icon={<Building2 className="w-4 h-4" />}
                    label="Manajemen Cabang"
                    active={currentView === 'BRANCHES'}
                    onClick={() => navigateTo('BRANCHES')}
                  />
                  <SidebarItem
                    icon={<UserCheck className="w-4 h-4" />}
                    label="User Manager"
                    active={currentView === 'USERS'}
                    onClick={() => navigateTo('USERS')}
                  />
                  {profile.role === UserRole.MASTER_ADMIN && (
                    <SidebarItem
                      icon={<Globe className="w-4 h-4" />}
                      label="Website Builder"
                      active={currentView === 'WEBSITE'}
                      onClick={() => navigateTo('WEBSITE')}
                    />
                  )}
                </>
              )}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-50 space-y-3">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
              <Cloud
                className={`w-3 h-3 ${isSyncing ? 'text-amber-500 animate-pulse' : 'text-emerald-500'}`}
              />
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                {isSyncing ? 'Syncing...' : 'Synced'}
              </span>
            </div>
            <button onClick={fetchData} className="p-1 hover:bg-white rounded-md transition-all">
              <RefreshCw className={`w-3 h-3 text-slate-300 ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 text-rose-500 font-black p-3 hover:bg-rose-50 rounded-2xl transition-all text-xs uppercase tracking-widest"
          >
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden text-slate-900">
        <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-4 md:px-8 shrink-0 relative z-50">
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 -ml-2 text-slate-500 hover:bg-slate-50 rounded-xl md:hidden transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>

            {isHQRole && (
              <select
                className="border-2 border-indigo-50 p-1.5 md:p-2 rounded-xl text-[9px] md:text-[10px] font-black bg-indigo-50 text-indigo-700 outline-none uppercase cursor-pointer hover:border-indigo-200 transition-all shadow-sm max-w-[120px] md:max-w-none"
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
              >
                <option value="ALL">SEMUA</option>
                <option value="HEADQUARTERS">PUSAT</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name.toUpperCase()}
                    {b.is_active === false ? ' (NONAKTIF)' : ''}
                  </option>
                ))}
              </select>
            )}
            <h2 className="font-black text-slate-700 uppercase tracking-widest text-[9px] md:text-[11px] hidden sm:block">
              {currentView.replace('_', ' ')}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-black leading-none">{profile.name.toUpperCase()}</p>
              <p className="text-[8px] font-bold text-slate-400 mt-1">{profile.role}</p>
            </div>
            <div className="w-9 h-9 md:w-10 md:h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-black shadow-lg shadow-indigo-100 ring-2 ring-white">
              {profile.name.charAt(0)}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 md:p-10 no-scrollbar bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto h-full">
            {currentView === 'DASHBOARD' && profile.role === UserRole.STUDENT ? (
              !studentData ? (
                isSyncing ? (
                  <div className="p-10 flex flex-col items-center justify-center text-slate-400 font-black h-full gap-4">
                    <Loader2 className="animate-spin w-8 h-8 text-indigo-600" />
                    <p className="uppercase tracking-widest text-[10px]">
                      Sinkronisasi Data Profil Siswa...
                    </p>
                  </div>
                ) : (
                  <div className="p-8 md:p-12 flex flex-col items-center justify-center text-slate-600 h-full gap-4 bg-white rounded-3xl border border-slate-100 shadow-sm text-center my-auto">
                    <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-500 mb-2">
                      <AlertCircle className="w-8 h-8" />
                    </div>
                    <h3 className="font-black text-lg text-slate-800 uppercase tracking-wide">
                      Data Profil Siswa Belum Ditemukan
                    </h3>
                    <p className="text-xs text-slate-500 max-w-md leading-relaxed">
                      Akun Anda ({profile.email || profile.name}) terdaftar sebagai <span className="font-bold text-slate-700">Siswa</span>, tetapi belum terhubung dengan data profil siswa di database cabang.
                    </p>
                    <p className="text-[11px] text-slate-400 max-w-sm">
                      Silakan hubungi Admin atau Staf Cabang Bimbel DELTA untuk mengaitkan akun Anda.
                    </p>
                    <button
                      onClick={fetchData}
                      className="mt-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-indigo-100"
                    >
                      <RefreshCw className="w-4 h-4" /> Sync Ulang Data
                    </button>
                  </div>
                )
              ) : (
                <StudentPortal student={studentData} branding={{ brand_name: 'DELTA' } as any} />
              )
            ) : currentView === 'DASHBOARD' ? (
              <Dashboard
                students={students}
                branchName={activeBranchName}
                role={profile.role}
                userId={profile.id}
                schedules={schedules}
              />
            ) : null}

            {currentView === 'STUDENTS' && (
              <StudentList
                students={students}
                branches={branches}
                activeBranchId={selectedBranchId}
                onAddStudent={fetchData}
                onDeleteStudent={fetchData}
              />
            )}
            {currentView === 'SCHEDULE' && (
              <ScheduleManager branchId={selectedBranchId} tentors={tentors} />
            )}
            {currentView === 'CLASS_ROOMS' && <ClassManager branchId={selectedBranchId} />}
            {currentView === 'TEACHERS' && <TeacherManager branchId={selectedBranchId} />}
            {currentView === 'FINANCE' && (
              <FinanceManager
                incomes={incomes}
                expenses={expenses}
                payments={pendingPayments}
                registrations={onlineRegistrations}
                employees={employees}
                tentors={tentors}
                students={students}
                branchId={selectedBranchId}
                activeBranchName={activeBranchName}
                onRefresh={fetchData}
              />
            )}
            {currentView === 'ATTENDANCE' && (
              <AttendanceManager students={students} branchId={selectedBranchId} />
            )}
            {currentView === 'MODULES' && <ModuleManager branchId={selectedBranchId} />}
            {currentView === 'EXAMS' && <ExamManager branchId={selectedBranchId} />}
            {currentView === 'REPORTS' && <ReportManager students={students} />}
            {currentView === 'BRANCHES' && <BranchManager />}
            {currentView === 'USERS' && (
              <UserManager branches={branches} currentUserRole={profile.role} />
            )}
            {currentView === 'WEBSITE' && (
              <WebsiteBuilder
                config={{ brand_name: 'DELTA' } as any}
                role={profile.role}
                onSave={async () => fetchData()}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const SidebarItem = ({
  icon,
  label,
  active,
  onClick,
}: {
  icon: any;
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all font-black text-[10px] uppercase tracking-widest mb-1 ${active ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100' : 'text-slate-400 hover:bg-indigo-50 hover:text-indigo-600'}`}
  >
    {icon}
    <span className="tracking-tight">{label}</span>
  </button>
);

export default App;
